import { IScore } from "@softgames/firebase-leaderboard/dist/types";
import { TweenManager } from "js/framework/TweenManager/TweenManager";
import { Layout } from "js/layouts/LeaderboardPopup";
import { game } from "js/main";
import { WaitingScreen } from "./WaitingScreen";
import { Game } from 'framework/Game';

interface IGameOverPopupConfig {
  parent: PIXI.Container;
  tweenMgr: TweenManager;
  unPauseGameLeaderboardScreen: () => unknown;
}

export class LeaderboardPopup {
  private game: Game;
  private readonly tweenMgr: TweenManager;
  private pageRange: number;
  private pageRangeArray: number[];
  private minRankLimitArray: number[];
  private maxRankLimitArray: number[];
  private minRankLimit: number;
  private maxRankLimit: number;
  private layout: Layout;

  constructor({
    parent,
    tweenMgr,
    unPauseGameLeaderboardScreen
  }: IGameOverPopupConfig) {
    this.game = game;
    this.tweenMgr = tweenMgr;
    this.pageRange = 1;
    this.pageRangeArray = [1, 2, 3, 4, 5];
    this.minRankLimitArray = [0, 10, 20, 30, 40];
    this.maxRankLimitArray = [9, 19, 29, 39, 49];
    
    this.getTop50Leaderboard({ parent })
      .then((leaderboardData) => {
        this.layout = new Layout();
        parent.addChild(this.layout.root);
    
        this.populateLeaderboard(leaderboardData);
        this.layout.continueButton.once('pointerdown', async () => {
          this.game.sounds.play("button_click");
          await this.hidePopup();
          unPauseGameLeaderboardScreen();
        });
        this.handleArrowButtons();
        this.layout.arrow_left.on('pointerdown', async () => {
          if (this.pageRange == 1) return;
          this.game.sounds.play("button_click");
          --this.pageRange;
          this.handleArrowButtons();
          this.populateLeaderboard(leaderboardData);
        });
        this.layout.arrow_right.on('pointerdown', async () => {
          if (this.pageRange == 5) return;
          this.game.sounds.play("button_click");
          ++this.pageRange;
          this.handleArrowButtons();
          this.populateLeaderboard(leaderboardData);
        });
    
        this.showPopup();
      });
  }

  private disableArrowButton(arrowButton) {
    this.layout[arrowButton].interactive = false;
    this.layout[arrowButton].buttonMode = false;
    this.layout[arrowButton].alpha = 0.3;
  }

  private enableArrowButton(arrowButton) {
    this.layout[arrowButton].interactive = true;
    this.layout[arrowButton].buttonMode = true;
    this.layout[arrowButton].alpha = 1;    
  }

  private handleArrowButtons() {
    this.enableArrowButton('arrow_left');
    this.enableArrowButton('arrow_right');
    if (this.pageRange == 1) this.disableArrowButton('arrow_left');
    if (this.pageRange == 5) this.disableArrowButton('arrow_right');
  }
  
  private async getTop50Leaderboard({
    parent
  }: {
    parent: PIXI.Container
  }) {
    const waitingScreen = new WaitingScreen(parent);

    const leaderboard = await game.leaderboard.getLeaderboard();

    waitingScreen.destroy();

    return leaderboard;
  }

  private populateLeaderboard(leaderboardData: IScore[]) {
    this.minRankLimit = this.minRankLimitArray[this.pageRangeArray.indexOf(this.pageRange)];
    this.maxRankLimit = this.maxRankLimitArray[this.pageRangeArray.indexOf(this.pageRange)];
    const setupScoreRank = (index: number, data: IScore, posReplacer?: string) => {
      const top3Index = [0, 1, 2];
      const actualIndex = index;
      if (index < this.minRankLimit || index > this.maxRankLimit) return;
      
      if (index > 9) index = index % 10;
      const scoreContainer = this.layout.root.getChildByName(`score_${index}`);
      scoreContainer.visible = false;

      if (!data) return;

      // declare everything
      const leaderboard_highlight: PIXI.Sprite = scoreContainer.getChildByName(`leaderboard_highlight_${index}`);
      const leaderboard_highlight_CP: PIXI.Sprite = scoreContainer.getChildByName(`leaderboard_highlight_CP_${index}`);
      const score_trophy: PIXI.Sprite = scoreContainer.getChildByName(`score_trophy_${index}`);

      const color = 0xFFFFFF;

      const pos: PIXI.Text = scoreContainer.getChildByName(`score_pos_${index}`);
      pos.tint = color;
      pos.text = posReplacer || `${actualIndex + 1}`;

      const name: PIXI.Text = scoreContainer.getChildByName(`score_name_${index}`);
      name.tint = color;
      name.text = data.name;

      const points: PIXI.Text = scoreContainer.getChildByName(`score_points_${index}`);
      points.tint = color;
      points.text = `${data.score}`;

      scoreContainer.visible = true;

      // reset everything for top 3 players & current player before setting them
      if (top3Index.includes(index)) {
        leaderboard_highlight.visible = false;
        score_trophy.visible = false;
      }
      leaderboard_highlight_CP.visible = false;

      pos.style.stroke = 'black';
      pos.style.fill = 'white';
      pos.style.strokeThickness = 0;
      pos.style.fontSize = 40;
      pos.y = 0;

      name.style.fill = 'white';
      name.style.stroke = '#black';
      name.style.strokeThickness = 0;

      points.style.fill = 'white';
      points.style.stroke = '#black';
      points.style.strokeThickness = 0;

      // set everything for top 3 players & current player
      if (top3Index.includes(actualIndex) && data.localUser) {
        leaderboard_highlight_CP.visible = true;
        score_trophy.visible = true;
        name.style.fill = 'black';
        points.style.fill = 'black';

        switch (actualIndex) {
          case 0:
            pos.style.stroke = '#D9690F';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 38;
            pos.y = -7;
            break;
          case 1:
            pos.style.stroke = '#5482AB';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 36;
            pos.y = -8;
            break;
          case 2:
            pos.style.stroke = '#864954';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 34;
            pos.y = -8;
            break;
          default:
            break;
        }
      } else if (top3Index.includes(actualIndex)) {
        leaderboard_highlight.visible = true;

        score_trophy.visible = true;
        name.style.fill = 'black';
        points.style.fill = 'black';

        switch (actualIndex) {
          case 0:
            pos.style.stroke = '#D9690F';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 38;
            pos.y = -7;
            break;
          case 1:
            pos.style.stroke = '#5482AB';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 36;
            pos.y = -8;
            break;
          case 2:
            pos.style.stroke = '#864954';
            pos.style.strokeThickness = 4;
            pos.style.fontSize = 34;
            pos.y = -8;
            break;
          default:
            break;
        }
      } else if (data.localUser) {
        leaderboard_highlight_CP.visible = true;
        pos.style.fill = 'black';
        name.style.fill = 'black';
        points.style.fill = 'black';
      }
    };

    // Fill all ranks
    const usedData = Array(50).fill(null).map((_, index) => {
      const data = leaderboardData[index];
      setupScoreRank(index, data);

      return data;
    }).filter(Boolean);

    // if (usedData.every(score => Boolean(score.localUser) === false)) {
    //   const lastIndex = Math.min(9, usedData.length);
    //   const userLeadData = leaderboardData.find(data => data.localUser);
    //   setupScoreRank(
    //     lastIndex,
    //     userLeadData!,
    //     userLeadData ? `${leaderboardData.indexOf(userLeadData) + 1}.` : "-",
    //   );
    // }
  }

  private showPopup() {
    this.tweenMgr.create(this.layout.root)
      .to({alpha: 1}, 300, this.tweenMgr.easing.Sinusoidal.Out, true);
  }

  private async hidePopup() {
    await this.tweenMgr.create(this.layout.root)
      .to({alpha: 0}, 300, this.tweenMgr.easing.Sinusoidal.Out, true)
      .asyncOnComplete();

    this.layout.root.destroy();
  }
}
